import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/lib/utils';
import type { Budget, UpdateBudgetRequest } from '@/types';

interface BudgetConflictComparatorProps {
  message?: string;
  serverBudget: Budget;
  myVersion: UpdateBudgetRequest;
  onMyVersionChange: (next: UpdateBudgetRequest) => void;
  onCopyServerToMine: () => void;
  onUseServerVersion: () => void;
  onApplyMyVersion: () => void;
  isApplying?: boolean;
}

function formatBoolean(value: boolean): string {
  return value ? 'Enabled' : 'Disabled';
}

function formatParent(parentId?: string): string {
  return parentId ?? 'No parent';
}

function formatAmount(value: number, currency: string): string {
  return formatCurrency(value, currency);
}

export function BudgetConflictComparator({
  message,
  serverBudget,
  myVersion,
  onMyVersionChange,
  onCopyServerToMine,
  onUseServerVersion,
  onApplyMyVersion,
  isApplying,
}: BudgetConflictComparatorProps) {
  const serverRollover = serverBudget.rolloverAmount ?? 0;
  const mineRollover = myVersion.rolloverAmount ?? 0;

  const changedCount = React.useMemo(() => {
    let count = 0;
    if (serverBudget.name !== myVersion.name) count++;
    if (serverBudget.type !== myVersion.type) count++;
    if ((serverBudget.parentId ?? undefined) !== myVersion.parentId) count++;
    if (serverBudget.currency !== myVersion.currency) count++;
    if (serverBudget.amount !== myVersion.amount) count++;
    if (serverBudget.startDate !== myVersion.startDate) count++;
    if (serverBudget.endDate !== myVersion.endDate) count++;
    if (serverBudget.rolloverEnabled !== myVersion.rolloverEnabled) count++;
    if (serverRollover !== mineRollover) count++;
    return count;
  }, [mineRollover, myVersion, serverBudget, serverRollover]);


  const updateMine = <K extends keyof UpdateBudgetRequest>(field: K, value: UpdateBudgetRequest[K]) => {
    onMyVersionChange({
      ...myVersion,
      [field]: value,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 p-4 md:p-8">
      <div className="mx-auto flex h-full w-full max-w-5xl flex-col rounded-lg border border-input bg-background shadow-lg">
        <div className="border-b border-input px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">Version conflict detected</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {message ?? 'This budget was updated elsewhere. Resolve this conflict to continue.'}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {changedCount} field{changedCount === 1 ? '' : 's'} differ between server and your version.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 flex justify-end">
            <Button type="button" variant="outline" onClick={onCopyServerToMine} disabled={isApplying}>
              Move Server Changes → My Version
            </Button>
          </div>

          <div className="rounded-md border border-input overflow-hidden">
            <div className="grid grid-cols-3 bg-muted/40 text-xs font-medium text-muted-foreground">
              <div className="px-3 py-2">Field</div>
              <div className="px-3 py-2 border-l border-input">Server</div>
              <div className="px-3 py-2 border-l border-input">My Version (Editable)</div>
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Name</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{serverBudget.name}</div>
              <div className="px-3 py-2 border-l border-input">
                <Input
                  value={myVersion.name}
                  onChange={(e) => updateMine('name', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Type</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{serverBudget.type}</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{myVersion.type}</div>
              {/* <div className="px-3 py-2 border-l border-input">
                <Select
                  options={typeOptions}
                  value={}
                  onChange={(e) => updateMine('type', e.target.value as UpdateBudgetRequest['type'])}
                />
              </div> */}
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Parent ID</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{formatParent(serverBudget.parentId ?? undefined)}</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{formatParent(myVersion.parentId ?? undefined)}</div>
              {/* <div className="px-3 py-2 border-l border-input">
                <Input
                  placeholder="No parent"
                  value={myVersion.parentId ?? ''}
                  onChange={(e) => updateMine('parentId', e.target.value.trim() || undefined)}
                />
              </div> */}
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Currency</div>
                <div className="px-3 py-3 border-l border-input text-foreground">{serverBudget.currency}</div>
                <div className="px-3 py-3 border-l border-input text-foreground">{myVersion.currency}</div>
              {/* <div className="px-3 py-2 border-l border-input">
                <Input
                  value={myVersion.currency}
                  maxLength={3}
                  onChange={(e) => updateMine('currency', e.target.value.toUpperCase())}
                />
              </div> */}
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Amount</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{formatAmount(serverBudget.amount, serverBudget.currency)}</div>
              <div className="px-3 py-2 border-l border-input">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={String(myVersion.amount)}
                  onChange={(e) => updateMine('amount', Number(e.target.value || 0))}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Start Date</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{serverBudget.startDate}</div>
              <div className="px-3 py-2 border-l border-input">
                <Input
                  type="date"
                  value={myVersion.startDate}
                  onChange={(e) => updateMine('startDate', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">End Date</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{serverBudget.endDate}</div>
              <div className="px-3 py-2 border-l border-input">
                <Input
                  type="date"
                  value={myVersion.endDate}
                  onChange={(e) => updateMine('endDate', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Rollover</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{formatBoolean(serverBudget.rolloverEnabled)}</div>
              <div className="px-3 py-3 border-l border-input">
                <label className="inline-flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={myVersion.rolloverEnabled}
                    onChange={(e) => updateMine('rolloverEnabled', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  {formatBoolean(myVersion.rolloverEnabled)}
                </label>
              </div>
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Rollover Amount</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{formatAmount(serverRollover, serverBudget.currency)}</div>
              <div className="px-3 py-2 border-l border-input">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={String(mineRollover)}
                  onChange={(e) => updateMine('rolloverAmount', Number(e.target.value || 0))}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 border-t border-input text-sm">
              <div className="px-3 py-3 font-medium text-foreground">Version</div>
              <div className="px-3 py-3 border-l border-input text-foreground">{serverBudget.version}</div>
              <div className="px-3 py-3 border-l border-input text-muted-foreground">
                {myVersion.version}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-input px-6 py-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2">
          <Button variant="outline" onClick={onUseServerVersion} disabled={isApplying}>
            Use Server Version
          </Button>
          <Button onClick={onApplyMyVersion} loading={isApplying}>
            Apply My Version
          </Button>
        </div>
      </div>
    </div>
  );
}